<template>
  <div
    v-if="show"
    class="fixed inset-0 z-[9999] overflow-hidden flex items-center justify-center bg-slate-800/80"
    @click.self="closeModal"
    role="dialog"
    aria-modal="true"
    aria-label="File viewer"
  >
    <!-- Close button (outside the image) -->
    <button
      @click="closeModal"
      class="absolute top-4 right-4 text-white hover:text-gray-200 z-10 p-2"
    >
      <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
      </svg>
    </button>

    <!-- File content container -->
    <div
      v-if="isImageFile && currentFile"
      class="relative w-full h-full flex items-center justify-center p-0"
      @click.self="closeModal"
    >
      <!-- Loading spinner -->
      <Loading :show="!imageLoaded" />

      <div class="max-w-full max-h-full overflow-auto flex items-center justify-center w-full h-full"
           @click.self="closeModal">
        <img
          :src="getOriginalImageUrl(currentFile)"
          :style="{
            maxHeight: '100%',
            maxWidth: '100%',
            height: 'auto',
            width: 'auto',
            display: 'block'
          }"
          alt="File preview"
          @load="imageLoaded = true"
          @error="handleImageError"
          @click.stop
        />
      </div>
    </div>

    <!-- Document viewers -->
    <div
      v-else
      class="relative bg-white dark:bg-slate-800 rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] flex flex-col"
    >
      <!-- Header -->
      <div class="flex items-center justify-between px-4 py-3 border-b border-slate-200 dark:border-slate-700">
        <h3 class="text-lg font-medium text-slate-900 dark:text-slate-100">{{ currentFile?.name }}</h3>
        <button @click="closeModal" class="text-slate-400 hover:text-slate-600 dark:text-slate-300">
          <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      <!-- Content -->
      <div class="grow overflow-auto p-4">
        <div v-if="isPdfFile" class="h-full flex flex-col items-center justify-center">
          <div v-if="pdfLoading" class="mb-4">
            <Loading :show="true" />
          </div>

          <iframe
            v-if="canPreviewFile && fileUrl"
            :src="fileUrl"
            class="w-full rounded border border-slate-200 dark:border-slate-700"
            style="height: 70vh;"
            @load="pdfLoading = false"
            @error="handlePreviewError"
          ></iframe>

          <div v-if="previewError || !canPreviewFile" class="flex flex-col items-center">
            <svg class="w-16 h-16 text-slate-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <p class="text-slate-600 dark:text-slate-300 mb-2">PDF file</p>
            <p v-if="previewError" class="text-red-500 text-sm mb-4">No se pudo cargar la vista previa</p>
            <a
              :href="currentFile?.url || currentFile?.cdnUrl"
              target="_blank"
              class="mt-2 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              Open PDF
            </a>
          </div>
        </div>
        <div v-else class="h-full flex flex-col items-center justify-center">
          <svg xmlns="http://www.w3.org/2000/svg" class="h-16 w-16 text-slate-400 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1" d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
          </svg>
          <p class="text-slate-600 dark:text-slate-300">{{ currentFile?.name || 'Document file' }}</p>
          <a
            :href="currentFile?.url || currentFile?.cdnUrl"
            target="_blank"
            class="mt-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Download File
          </a>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue';
import Loading from '../../../components/loading.vue';

const props = defineProps({
  show: {
    type: Boolean,
    default: false
  },
  file: {
    type: Object,
    default: () => ({})
  }
});

const emit = defineEmits(['close']);

const imageLoaded = ref(false);
const pdfLoading = ref(true);
const previewError = ref(false);

const showModal = computed(() => props.show);
const currentFile = computed(() => props.file);

const fileUrl = computed(() => {
    if (!currentFile.value) {
      return null;
    }
    if (isPdfFile && currentFile.value?.name) {
        return `${currentFile.value?.url}/${currentFile.value?.name}`;
    }
    if (isImageFile) {
      return `${currentFile.value?.url}/_original/${currentFile.value?.fileName}` || currentFile.value?.cdnUrl || currentFile.value?.previewUrl;
    }
    return `${currentFile.value?.url}/${currentFile.value?.fileName}`;
});

const canPreviewFile = computed(() => {
  if (!fileUrl.value) return false;
  return isPdfFile.value;
});

const isImageFile = computed(() => {
  const fileType = currentFile.value?.type || currentFile.value?.mime_type || '';
  const fileName = currentFile.value?.name || currentFile.value?.file_name || '';
  return fileType.includes('image') || /\.(jpg|jpeg|png|gif|webp|svg)$/i.test(fileName);
});

const isPdfFile = computed(() => {
  const fileType = currentFile.value?.type || currentFile.value?.mime_type || '';
  const fileName = currentFile.value?.name || currentFile.value?.file_name || '';

  return fileType.includes('pdf') || fileName.toLowerCase().endsWith('.pdf');
});


function getOriginalImageUrl(file) {
  if (file?.sizes?.original) {
    return file.sizes.original;
  }

  if (file?.url && file?.fileName) {
    return `${file?.url}/_original/${file?.fileName}`;
  }

  return `${file?.url}/_original/${file?.name}`;
}

function handleImageError(event) {
  console.error('Error loading image:', event);
  imageLoaded.value = true;
}

function handleEscKey(event) {
  if (event.key === 'Escape' && showModal.value) {
    closeModal();
  }
}

onMounted(() => {
  document.addEventListener('keydown', handleEscKey);
});

onBeforeUnmount(() => {
  document.removeEventListener('keydown', handleEscKey);
});

function handlePreviewError(event) {
  console.error('Error loading PDF preview:', event);
  previewError.value = true;
  pdfLoading.value = false;
}

function closeModal() {
  emit('close');
  imageLoaded.value = false;
  pdfLoading.value = true;
  previewError.value = false;
}

watch(() => props.file, () => {
  imageLoaded.value = false;
});
</script>
